import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useAuth } from '../../state/auth'

const schema = z.object({
  email: z.string().email('Informe um email válido'),
  password: z.string().min(6, 'Mínimo de 6 caracteres')
})

type FormData = z.infer<typeof schema>

export function LoginPage() {
  const { token, me, login } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  useEffect(() => {
    if (token) {
      const dest = me?.userType === 'cliente' ? '/funcionarios' : '/'
      navigate(dest, { replace: true })
    }
  }, [token, me?.userType, navigate])

  const onSubmit = async (data: FormData) => {
    const logged = await login(data.email.trim().toLowerCase(), data.password.trim())
    navigate(logged.userType === 'cliente' ? '/funcionarios' : '/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="mx-auto flex min-h-screen max-w-6xl items-center px-4">
        <div className="grid w-full grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="text-white">
            <div className="text-2xl font-semibold tracking-tight">NCC Sistema RH</div>
            <div className="mt-3 max-w-md text-sm text-slate-300">
              Plataforma online de RH com multi-empresa, controle de acesso por perfil, histórico de
              cálculos e recursos de LGPD.
            </div>
            <div className="mt-6 text-xs text-slate-400">
              Ao continuar, você concorda com os{' '}
              <Link className="underline" to="/termos">
                Termos de Uso
              </Link>{' '}
              e a{' '}
              <Link className="underline" to="/privacidade">
                Política de Privacidade
              </Link>
              .
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-xl lg:p-8">
            <div className="text-lg font-semibold text-slate-900">Acessar</div>
            <div className="mt-1 text-sm text-slate-500">Use seu email e senha.</div>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="text-sm font-medium text-slate-700">Email</label>
                <input
                  type="email"
                  autoComplete="email"
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 outline-none focus:border-slate-900"
                  placeholder="voce@empresa.com"
                  {...register('email')}
                />
                {errors.email && <div className="mt-1 text-xs text-red-600">{errors.email.message}</div>}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Senha</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 outline-none focus:border-slate-900"
                  placeholder="••••••••"
                  {...register('password')}
                />
                {errors.password && (
                  <div className="mt-1 text-xs text-red-600">{errors.password.message}</div>
                )}
              </div>

              <button
                disabled={isSubmitting}
                className="h-11 w-full rounded-xl bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                type="submit"
              >
                {isSubmitting ? 'Entrando…' : 'Entrar'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

